import { useEffect, useState } from "react";
import { Star } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Link } from "react-router-dom";

const STAR_SIZE_DEFAULT = 15;
const STAR_SIZE_SM = 13;

const Stars = ({ value, onChange, size = STAR_SIZE_DEFAULT }) => (
  <div className="flex gap-0.5">
    {[1, 2, 3, 4, 5].map((n) => (
      <button
        key={n}
        type="button"
        data-testid={onChange ? `review-star-${n}` : undefined}
        disabled={!onChange}
        onClick={() => onChange?.(n)}
        className={onChange ? "cursor-pointer" : "cursor-default"}
        aria-label={`${n} stars`}
      >
        <Star
          size={size}
          strokeWidth={1.5}
          className={n <= value ? "text-capra-gold" : "text-capra-muted/40"}
          fill={n <= value ? "currentColor" : "none"}
        />
      </button>
    ))}
  </div>
);

const ReviewForm = ({ onSubmitted, slug }) => {
  const [rating, setRating] = useState(5);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post(`/products/${slug}/reviews`, { rating, title, body });
      onSubmitted(data);
      setTitle("");
      setBody("");
      toast.success("Thank you for your review.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not submit review.");
    }
    setBusy(false);
  };

  return (
    <form onSubmit={submit} className="leather-panel p-6 mb-10 space-y-4" data-testid="review-form">
      <div className="flex items-center gap-3">
        <span className="text-[10px] uppercase tracking-[0.25em] text-capra-muted">Your rating</span>
        <Stars value={rating} onChange={setRating} size={18} />
      </div>
      <input
        data-testid="review-title-input"
        required
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder="Title"
        className="w-full bg-capra-night/60 border border-white/10 px-4 py-2.5 text-sm text-capra-ivory placeholder:text-capra-muted/50 outline-none focus:border-capra-bronze transition-colors"
      />
      <textarea
        data-testid="review-body-input"
        required
        rows={3}
        value={body}
        onChange={(e) => setBody(e.target.value)}
        placeholder="Share your impressions…"
        className="w-full bg-capra-night/60 border border-white/10 px-4 py-2.5 text-sm text-capra-ivory placeholder:text-capra-muted/50 outline-none focus:border-capra-bronze transition-colors resize-none"
      />
      <button
        data-testid="review-submit-button"
        disabled={busy}
        className="px-6 py-2.5 bg-capra-bronze text-capra-night text-[11px] uppercase tracking-[0.2em] font-semibold hover:bg-capra-amber transition-colors disabled:opacity-60"
      >
        Submit Review
      </button>
    </form>
  );
};

const ReviewList = ({ reviews }) => {
  if (reviews.length === 0) {
    return <p className="text-sm text-capra-muted/70 italic">No impressions yet. Be the first to speak.</p>;
  }
  return (
    <div className="space-y-6">
      {reviews.map((r) => (
        <div key={r.review_id} className="border-b border-white/5 pb-6" data-testid={`review-${r.review_id}`}>
          <div className="flex items-center gap-3 mb-2">
            <Stars value={r.rating} size={STAR_SIZE_SM} />
            <span className="text-xs text-capra-muted">{r.user_name}</span>
          </div>
          <p className="font-display text-base text-capra-ivory mb-1">{r.title}</p>
          <p className="text-sm text-capra-muted leading-relaxed">{r.body}</p>
        </div>
      ))}
    </div>
  );
};

export const ReviewsSection = ({ slug, ratingAvg, ratingCount }) => {
  const { user } = useAuth();
  const [reviews, setReviews] = useState([]);

  useEffect(() => {
    api.get(`/products/${slug}/reviews`).then(({ data }) => setReviews(data)).catch(() => {});
  }, [slug]);

  return (
    <section className="mt-20" data-testid="reviews-section">
      <div className="flex items-baseline justify-between mb-8">
        <h2 className="font-display text-2xl text-capra-ivory">Client Impressions</h2>
        {ratingCount > 0 && (
          <div className="flex items-center gap-2 text-sm text-capra-muted">
            <Stars value={Math.round(ratingAvg)} />
            <span>{ratingAvg.toFixed(1)} · {ratingCount} review{ratingCount !== 1 ? "s" : ""}</span>
          </div>
        )}
      </div>

      {user ? (
        <ReviewForm slug={slug} onSubmitted={(r) => setReviews((prev) => [r, ...prev])} />
      ) : (
        <p className="text-sm text-capra-muted mb-10">
          <Link to="/login" className="text-capra-bronze hover:text-capra-amber transition-colors">Sign in</Link> to share your impressions.
        </p>
      )}

      <ReviewList reviews={reviews} />
    </section>
  );
};
